/** Shared bits for the tech Lookup tab. */
import { ChevronRight, ClipboardCheck, Phone, Mail, User } from "lucide-react";
import { format } from "date-fns";

const WO_STATUS = {
  draft: ["Draft", "bg-slate-100 text-slate-700"], approved: ["Approved", "bg-blue-100 text-blue-700"],
  scheduled: ["Scheduled", "bg-indigo-100 text-indigo-700"], en_route: ["En Route", "bg-purple-100 text-purple-700"],
  on_site: ["On Site", "bg-amber-100 text-amber-700"], in_progress: ["In Progress", "bg-amber-100 text-amber-700"],
  done_for_day: ["Done for Day", "bg-slate-200 text-slate-700"], completed: ["Complete", "bg-green-100 text-green-700"],
  invoiced: ["Invoiced", "bg-emerald-100 text-emerald-800"], paid: ["Paid", "bg-emerald-100 text-emerald-800"],
  cancelled: ["Cancelled", "bg-rose-100 text-rose-700"], sent: ["Sent", "bg-sky-100 text-sky-700"],
};
export function StatusPill({ status, testId }) {
  const [label, cls] = WO_STATUS[status] || [(status || "—").replace(/_/g, " "), "bg-slate-100 text-slate-600"];
  return <span className={`inline-flex items-center rounded px-1.5 py-0 text-[9px] font-bold uppercase tracking-wide ${cls}`} data-testid={testId}>{label}</span>;
}

export const fmtDate = (s) => { if (!s) return "—"; try { return format(new Date(s.length === 10 ? `${s}T12:00:00` : s), "MMM d, yyyy"); } catch { return s; } };

export function Section({ title, count, children, testId, right }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white" data-testid={testId}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
        <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wide">{title}</h3>
        {count != null && <span className="text-[10px] text-slate-400">{count}</span>}
        {right && <div className="ml-auto">{right}</div>}
      </div>
      <div className="p-2">{children}</div>
    </section>
  );
}

export function Empty({ text }) { return <div className="text-xs text-slate-400 py-2 text-center">{text}</div>; }

export function Row({ onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className="w-full flex items-center gap-2 text-left rounded-md px-2 py-1.5 hover:bg-slate-50 active:bg-slate-100 border-b border-slate-100 last:border-b-0">
      <div className="flex-1 min-w-0">{children}</div>
      {onClick && <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
    </button>
  );
}

export function WORow({ wo, onOpen, showClient }) {
  return (
    <Row onClick={() => onOpen(wo.id)} testId={`lookup-wo-${wo.id}`}>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-[10px] font-bold text-blue-600 shrink-0">{wo.wo_number || "—"}</span>
        <span className="text-xs font-semibold text-slate-800 truncate">{wo.title || "Untitled"}</span>
        <StatusPill status={wo.status} />
      </div>
      <div className="text-[10px] text-slate-500 truncate">
        {showClient && wo.client_name ? `${wo.client_name} · ` : ""}{wo.site_name ? `${wo.site_name} · ` : ""}
        {wo.last_visit ? `Last visit ${fmtDate(wo.last_visit)}` : `Created ${fmtDate(wo.created_at)}`}
        {wo.techs?.length ? ` · ${wo.techs.join(", ")}` : ""}
      </div>
    </Row>
  );
}

export function InspectionRow({ insp, onOpen }) {
  const done = insp.status === "complete";
  return (
    <Row onClick={() => onOpen(insp.id)} testId={`lookup-insp-${insp.id}`}>
      <div className="flex items-center gap-1.5 min-w-0">
        <ClipboardCheck className={`w-3.5 h-3.5 shrink-0 ${done ? "text-emerald-600" : "text-amber-600"}`} />
        <span className="text-xs font-semibold text-slate-800 truncate">{insp.template_name || (insp.is_dot ? "DOT Inspection" : "Inspection")}</span>
        <span className={`text-[9px] font-bold uppercase rounded px-1 ${done ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{done ? "Complete" : (insp.status || "in progress").replace(/_/g, " ")}</span>
      </div>
      <div className="text-[10px] text-slate-500">{insp.wo_number ? `${insp.wo_number} · ` : ""}{fmtDate(insp.completed_at || insp.conducted_date || insp.created_at)}</div>
    </Row>
  );
}

export function VisitRow({ v }) {
  return (
    <div className="flex items-center gap-2 px-2 py-1 text-[11px] border-b border-slate-100 last:border-b-0" data-testid={`lookup-visit-${v.id}`}>
      <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: v.tech_color }} />
      <span className="font-semibold text-slate-800 w-24 shrink-0">{fmtDate(v.date)}</span>
      <span className="text-slate-700 truncate flex-1">{v.tech_name}</span>
      <span className="text-slate-400">{v.logged_hours != null ? `${v.logged_hours}h logged` : v.hours != null ? `${v.hours}h` : ""}</span>
      <StatusPill status={v.status} />
    </div>
  );
}

export function ContactRow({ c }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-2 py-1 text-[11px] border-b border-slate-100 last:border-b-0" data-testid={`lookup-contact-${c.id || c.name}`}>
      <span className="inline-flex items-center gap-1 font-semibold text-slate-800"><User className="w-3 h-3 text-slate-400" />{c.name || "—"}{c.is_primary ? <span className="text-[8px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1">primary</span> : null}</span>
      {c.role && <span className="text-slate-400">{c.role}</span>}
      {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 text-blue-600 hover:underline"><Phone className="w-3 h-3" />{c.phone}</a>}
      {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 text-blue-600 hover:underline break-all"><Mail className="w-3 h-3" />{c.email}</a>}
    </div>
  );
}
